<template>
  <div>
    <DxFileManager
      :file-system-provider="remoteProvider"
      :on-selected-file-opened="displayImagePopup"
      ref="fileManager"
      :on-toolbar-item-click="fileManager_onToolbarItemClick"
      current-path="Widescreen"
    >
      <DxPermissions
        :create="true"
        :copy="true"
        :move="true"
        :delete="true"
        :rename="true"
        :select="true"
        :upload="true"
        :download="true"
      />

      <DxItemView :show-parent-folder="false">
        <DxDetails>
          <DxColumn data-field="thumbnail" />
          <DxColumn data-field="name" />
          <DxColumn data-field="category" caption="Category" :width="95" />
          <DxColumn data-field="dateModified" />
          <DxColumn data-field="size" />
        </DxDetails>
      </DxItemView>

      <DxToolbar>
        <DxItem name="showNavPane" :visible="true" />
        <DxItem name="separator" />
        <DxItem name="create" />
        <DxItem name="upload" />
        <DxItem widget="dxMenu" location="before" />
        <DxItem name="refresh" />
        <DxItem name="separator" location="after" />
        <DxItem name="switchView" />

        <DxFileSelectionItem name="rename" />
        <DxFileSelectionItem name="separator" />
        <DxFileSelectionItem name="delete" />
        <DxFileSelectionItem name="separator" />
        <DxFileSelectionItem name="insertImage" :options="fileOptions" />
        <DxFileSelectionItem widget="dxMenu" location="before" />
        <DxFileSelectionItem name="refresh" />
        <DxFileSelectionItem name="clearSelection" />
      </DxToolbar>
      <DxContextMenu>
        <DxItem name="rename" :begin-group="true" />
        <DxItem name="download" />
        <DxItem name="delete" />
      </DxContextMenu>
    </DxFileManager>
    <DxPopup
      :hide-on-outside-click="true"
      v-model:visible="popupVisible"
      v-model:title="imageItemToDisplay.name"
      max-height="600"
      class="photo-popup-content"
    >
      <img :src="imageItemToDisplay.url" class="photo-popup-image" />
    </DxPopup>
  </div>
</template>
<script lang="ts">
/* eslint-disable */
import { defineComponent, computed, onMounted } from "vue";
import {
  DxFileManager,
  DxPermissions,
  DxToolbar,
  DxContextMenu,
  DxItem,
  DxFileSelectionItem,
  DxItemView,
  DxDetails,
  DxColumn
} from "devextreme-vue/file-manager";
import { DxPopup } from "devextreme-vue/popup";
import RemoteFileSystemProvider from "devextreme/file_management/remote_provider";
import { Deferred } from "devextreme/core/utils/deferred";
import "devextreme/dist/css/dx.light.css";
import "devextreme-intl";
import twMessages from "@/locales/devextreme/zh-tw.json";
import { AuthorizationType } from "@cloudfun/core";
import model from "@/models";
import { locale, loadMessages } from "devextreme/localization";

const arg = JSON.stringify({
  pathInfo: [{ key: "resources", name: "resources" }]
});
const authorizeAjax = (options: { headers?: any }) => {
  options.headers = {
    ...options.headers,
    Authorization: `Basic ${model.getCertificate(AuthorizationType.Basic)}`
  };
};

// 解析 Content-Disposition 取得檔名（支援 filename*=UTF-8''...）
const parseFileName = (disposition: string | null): string | null => {
  if (!disposition) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch (e) {
      return star[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
};

// RemoteFileSystemProvider.downloadItems 以隱藏 <form> POST 提交，瀏覽器表單無法附加
// Authorization 標頭。改用 getItemsContent（XHR，會經過 beforeAjaxSend 附加標頭，
// 與 Download 命令送出相同的 command/arguments），再以 blob 觸發瀏覽器儲存。
class AuthenticatedRemoteFileSystemProvider extends RemoteFileSystemProvider {
  downloadItems(items: any[]): any {
    const deferred: any = Deferred();
    (this.getItemsContent(items) as any)
      .done((data: ArrayBuffer, _status: string, xhr: XMLHttpRequest) => {
        const contentType =
          xhr.getResponseHeader("Content-Type") || "application/octet-stream";
        if (/json/i.test(contentType)) {
          deferred.reject({ errorCode: 32767, errorText: "下載失敗" });
          return;
        }
        const fileName =
          parseFileName(xhr.getResponseHeader("Content-Disposition")) ||
          (items.length === 1 ? items[0].name : "files.zip");
        const url = URL.createObjectURL(new Blob([data], { type: contentType }));
        const link = document.createElement("a");
        link.style.display = "none";
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
          URL.revokeObjectURL(url);
        });
        deferred.resolve();
      })
      .fail((xhr: XMLHttpRequest) => {
        deferred.reject({
          errorCode: 32767,
          errorText: `下載失敗 (${(xhr && xhr.status) || "網路錯誤"})`
        });
      });
    return deferred.promise();
  }
}

const remoteProvider = new AuthenticatedRemoteFileSystemProvider({
  endpointUrl: `${process.env.VUE_APP_BACKEND_URL}/api/FileManager/FileManagement`,
  // 與其他 API 相同，使用 @cloudfun/core 模型的憑證附加 Authorization 標頭
  beforeAjaxSend: authorizeAjax
});

export default defineComponent({
  name: "FileManager",
  components: {
    DxFileManager,
    DxPermissions,
    DxToolbar,
    DxContextMenu,
    DxItem,
    DxFileSelectionItem,
    DxItemView,
    DxDetails,
    DxColumn,
    DxPopup
  },
  mounted() {},
  data() {
    loadMessages(twMessages);
    locale("zh-tw");
    return {
      remoteProvider,
      popupVisible: false,
      imageItemToDisplay: {},
      fileOptions: {
        text: "Insert",
        icon: "plus",
        onClick: this.handleCustomButtonClick
      }
    };
  },
  methods: {
    handleCustomButtonClick: function(e: any) {
      var fileManager = e.component;
      // console.log("CLICK!", fileManager);
      //       var selectedFiles = fileManager.getSelectedItems();
      // // var selectedFiles = e.component.getSelectedItems();
      // // 遍歷選定的文件
      // for (var i = 0; i < selectedFiles.length; i++) {
      //   // 顯示文件名和大小
      //   console.log(selectedFiles[i].name + ' - ' + selectedFiles[i].size);
      // }
    },
    fileManager_onToolbarItemClick(e: any) {
      // console.log(e);
      if (e.itemData.name === "insertImage") {
        // console.log(e.component.getSelectedItems())
        var imgArray = e.component.getSelectedItems();
        this.$emit("insert", imgArray);
      }
      // if (extension) {
      //   updated = this.createFile(extension, fileSystemItem);
      // } else if (category !== undefined) {
      //   updated = this.updateCategory(category, fileSystemItem, viewArea);
      // }

      // if (updated) {
      //   this.$refs.fileManager.instance.refresh(
      // }
    },
    displayImagePopup(e: any) {
      this.imageItemToDisplay = {
        name: e.file.name,
        url: e.file.dataItem.url
      };
      this.popupVisible = true;
    },
    customizeIcon(fileSystemItem: any) {
      console.log("file:", fileSystemItem);
      if (fileSystemItem.isDirectory) {
        return "images/thumbnails/folder.svg";
      }

      const fileExtension = fileSystemItem.getFileExtension();
      switch (fileExtension) {
        case ".txt":
          return "images/thumbnails/doc-txt.svg";
        case ".rtf":
          return "images/thumbnails/doc-rtf.svg";
        case ".xml":
          return "images/thumbnails/doc-xml.svg";
        default:
          return "images/thumbnails/doc-txt.svg";
      }
    }
  }
});
</script>
<style>
.dx-popup-content {
  text-align: center;
}

.dx-popup-content .photo-popup-image {
  height: auto;
  width: 100%;
}
</style>
